(() => {
  if (customElements.get('pw-store-locator')) return;
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const miles = (a, b) => {
    const radians = value => value * Math.PI / 180;
    const h = Math.sin(radians(b.lat - a.lat) / 2) ** 2 + Math.cos(radians(a.lat)) * Math.cos(radians(b.lat)) * Math.sin(radians(b.lng - a.lng) / 2) ** 2;
    return 3958.7613 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const safeWebsite = value => {
    try { const url = new URL(value); return ['http:', 'https:'].includes(url.protocol) ? url.href : null; } catch { return null; }
  };
  let mapsPromise;
  function loadMaps(key) {
    if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
    if (mapsPromise) return mapsPromise;
    mapsPromise = new Promise((resolve, reject) => {
      if (!key) { reject(new Error('No map key')); return; }
      const timer = setTimeout(() => reject(new Error('Map timed out')), 20000);
      window.pwLocatorMapsReady = () => { clearTimeout(timer); resolve(window.google.maps); };
      const previousFailure = window.gm_authFailure;
      window.gm_authFailure = () => {
        clearTimeout(timer);
        reject(new Error('Map authorization failed'));
        window.dispatchEvent(new Event('pw:locator-map-error'));
        previousFailure?.();
      };
      const script = document.createElement('script');
      const params = new URLSearchParams({ key, loading: 'async', v: 'weekly', callback: 'pwLocatorMapsReady' });
      script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
      script.async = true;
      script.onerror = () => { clearTimeout(timer); reject(new Error('Map unavailable')); };
      document.head.append(script);
    });
    return mapsPromise;
  }
  class StoreLocator extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.stores = [];
      this.matches = [];
      this.sequence = 0;
      this.pageSize = 20;
      this.origin = null;
      this.query = this.querySelector('input');
      this.status = this.querySelector('[data-status]');
      this.notice = this.querySelector('[data-notice]');
      this.results = this.querySelector('[data-results]');
      this.radius = this.querySelector('[data-radius]');
      this.mapNode = this.querySelector('[data-map]');
      this.near = this.querySelector('[data-near]');
      this.more = this.querySelector('[data-more]');
      this.querySelector('form').addEventListener('submit', event => { event.preventDefault(); this.search(); });
      this.querySelector('[data-reset]').addEventListener('click', () => this.reset());
      this.more.addEventListener('click', () => { this.pageSize += 20; this.renderList(); });
      this.radius.addEventListener('change', () => this.applyDistance());
      this.near.addEventListener('click', () => this.locate());
      this.querySelectorAll('[data-view]').forEach(button => button.addEventListener('click', () => this.setView(button.dataset.view)));
      this.mapErrorHandler = () => this.mapFailure();
      window.addEventListener('pw:locator-map-error', this.mapErrorHandler);
      this.load();
    }
    disconnectedCallback() { window.removeEventListener('pw:locator-map-error', this.mapErrorHandler); }
    async load() {
      const mapReady = loadMaps(this.dataset.mapsKey).then(maps => {
        if (!this.isConnected) return;
        this.maps = maps;
        this.map = new maps.Map(this.mapNode, { center: { lat: 39, lng: -98 }, zoom: 4, mapTypeControl: false, streetViewControl: false, fullscreenControl: true, gestureHandling: 'cooperative' });
        this.geocoder = new maps.Geocoder();
        this.info = new maps.InfoWindow();
        this.map.data.addListener('click', event => this.selectStore(String(event.feature.getId())));
        this.renderMap(false);
      }).catch(() => this.mapFailure());
      try {
        const locations = await this.loadLocations();
        this.stores = locations.map(store => ({ ...store, search: normalize([store.name, store.address, store.city, store.state, store.zip].join(' ')), nameSearch: normalize(store.name) })).sort((a, b) => a.name.localeCompare(b.name));
        this.reset();
      } catch {
        this.status.textContent = 'Store list could not load.';
        this.showNotice('Please refresh to try again, or email wholesale@psychedelicwater.com for help finding a store.');
        this.querySelector('button[type=submit]').disabled = true;
        this.near.disabled = true;
      }
      await mapReady;
    }
    async loadLocations() {
      let url = new URL(this.dataset.locationsUrl, window.location.href);
      const firstUrl = url;
      const visited = new Set();
      const stores = new Map();
      while (url) {
        if (visited.has(url.href) || visited.size >= 100) throw new Error('Invalid location pagination');
        visited.add(url.href);
        const response = await fetch(url.href, { cache: 'no-cache' });
        if (!response.ok) throw new Error('Location data unavailable');
        const data = await response.json();
        if (data.configured === false || !Array.isArray(data.locations)) throw new Error('Invalid location data');
        for (const store of data.locations) {
          const lat = Number(store.lat), lng = Number(store.lng);
          if (store.lat === null || store.lng === null || store.lat === '' || store.lng === '' || !Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180 || !store.id || typeof store.name !== 'string') throw new Error('Invalid store record');
          stores.set(String(store.id), { ...store, id: String(store.id), lat, lng });
        }
        if (!data.next) break;
        url = new URL(data.next, url);
        if (url.origin !== firstUrl.origin || url.pathname !== firstUrl.pathname) throw new Error('Unexpected location feed');
        if (firstUrl.searchParams.has('view')) url.searchParams.set('view', firstUrl.searchParams.get('view'));
      }
      return [...stores.values()];
    }
    showNotice(message = '') { this.notice.textContent = message; this.notice.hidden = !message; }
    mapFailure() {
      this.maps = null; this.geocoder = null; this.map = null;
      this.mapNode.replaceChildren(element('p', '', 'The map is unavailable right now. You can still search the store list and open directions.'));
    }
    reset() {
      this.sequence++;
      this.near.disabled = false;
      this.query.value = '';
      this.origin = null;
      this.radius.disabled = true;
      this.radius.value = '25';
      this.matches = [...this.stores];
      this.selected = null;
      this.pageSize = 20;
      this.showNotice();
      this.status.textContent = `${this.stores.length.toLocaleString()} stores. Find one near you.`;
      this.renderList(); this.renderMap(false);
      this.querySelector('.pw-locator__list-panel').scrollTop = 0;
      if (this.map) { this.map.setCenter({ lat: 39, lng: -98 }); this.map.setZoom(4); }
    }
    async search() {
      const text = this.query.value.trim();
      if (!text) { this.reset(); return; }
      const token = ++this.sequence;
      this.near.disabled = false;
      this.showNotice();
      const term = normalize(text);
      if (!term) { this.showNotice('Enter a city, ZIP code or store name.'); return; }
      const local = this.stores.filter(store => store.search.includes(term));
      const named = this.stores.filter(store => store.nameSearch.includes(term));
      this.origin = null; this.radius.disabled = true;
      if (named.length || !this.geocoder) {
        this.matches = named.length ? named : local;
        this.status.textContent = `${this.matches.length} stores matching “${text}”`;
        if (!this.geocoder) this.showNotice('Searching store names and addresses. Nearby search will return when the map connection is available.');
        this.updateResults(); return;
      }
      this.status.textContent = 'Finding nearby stores…';
      try {
        const response = await this.geocoder.geocode({ address: text, region: 'us' });
        if (token !== this.sequence) return;
        const result = response.results[0];
        if (!result) throw new Error('No matching place');
        this.origin = result.geometry.location.toJSON();
        this.placeLabel = result.formatted_address;
        this.radius.disabled = false;
        this.applyDistance();
      } catch {
        if (token !== this.sequence) return;
        this.matches = local;
        this.status.textContent = `${local.length} stores matching “${text}”`;
        this.showNotice('We could not locate that city or ZIP. Showing matching store addresses instead. Try adding a state to your search.');
        this.updateResults();
      }
    }
    applyDistance() {
      if (!this.origin) return;
      const limit = this.radius.value === 'all' ? Infinity : Number(this.radius.value);
      this.matches = this.stores.map(store => ({ ...store, distance: miles(this.origin, store) })).filter(store => store.distance <= limit).sort((a, b) => a.distance - b.distance || a.name.localeCompare(b.name));
      this.status.textContent = `${this.matches.length} stores ${Number.isFinite(limit) ? `within ${limit} miles of` : 'nearest to'} ${this.placeLabel}`;
      this.updateResults();
    }
    locate() {
      if (!navigator.geolocation) { this.showNotice('Location is unavailable in this browser. Enter your city or ZIP instead.'); return; }
      const token = ++this.sequence;
      this.near.disabled = true;
      this.showNotice('Your browser will ask to use your location to find nearby stores.');
      navigator.geolocation.getCurrentPosition(position => {
        if (token !== this.sequence) return;
        this.near.disabled = false;
        this.showNotice();
        this.origin = { lat: position.coords.latitude, lng: position.coords.longitude };
        this.placeLabel = 'your location';
        this.query.value = '';
        this.radius.disabled = false;
        this.applyDistance();
      }, () => {
        if (token !== this.sequence) return;
        this.near.disabled = false;
        this.showNotice('Your location could not be shared. Enter your city or ZIP to find stores.');
      }, { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000 });
    }
    updateResults() { this.selected = null; this.pageSize = 20; this.renderList(); this.renderMap(true); this.querySelector('.pw-locator__list-panel').scrollTop = 0; }
    directions(store) { return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${store.lat},${store.lng}`)}`; }
    makeLink(label, href) {
      const link = element('a', '', label); link.href = href;
      if (href.startsWith('http')) { link.target = '_blank'; link.rel = 'noopener noreferrer'; }
      return link;
    }
    renderList() {
      const fragment = document.createDocumentFragment();
      this.matches.slice(0, this.pageSize).forEach((store, index) => {
        const card = element('li', 'pw-locator__card'); card.dataset.storeId = String(store.id); card.dataset.selected = String(String(store.id) === this.selected);
        const top = element('div', 'pw-locator__card-top');
        top.append(element('span', 'pw-locator__number', String(index + 1)));
        const title = element('div');
        const name = element('button', 'pw-locator__name', store.name); name.type = 'button'; name.setAttribute('aria-label', `Show ${store.name} on map`); name.addEventListener('click', () => { this.setView('map'); this.selectStore(String(store.id)); });
        title.append(name);
        if (Number.isFinite(store.distance)) title.append(element('span', 'pw-locator__distance', `${store.distance < .1 ? 'Less than 0.1' : store.distance.toFixed(1)} miles away`));
        top.append(title); card.append(top);
        card.append(element('address', 'pw-locator__address', [store.address, [store.city, store.state, store.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')));
        const links = element('div', 'pw-locator__links'); links.append(this.makeLink('Directions', this.directions(store)));
        if (store.phone && /[0-9]/.test(store.phone)) links.append(this.makeLink('Call store', `tel:${store.phone.replace(/[^+0-9]/g, '')}`));
        const site = safeWebsite(store.website); if (site) links.append(this.makeLink('Website', site));
        card.append(links); fragment.append(card);
      });
      this.results.replaceChildren(fragment);
      this.more.hidden = this.matches.length <= this.pageSize;
      this.querySelector('[data-empty]').hidden = this.matches.length !== 0;
    }
    renderMap(fit) {
      if (!this.map || !this.maps) return;
      this.info?.close();
      this.map.data.forEach(feature => this.map.data.remove(feature));
      this.map.data.addGeoJson({ type: 'FeatureCollection', features: this.matches.map(store => ({ type: 'Feature', id: String(store.id), geometry: { type: 'Point', coordinates: [store.lng, store.lat] }, properties: { name: store.name } })) });
      this.map.data.setStyle(feature => ({ title: feature.getProperty('name'), icon: { path: this.maps.SymbolPath.CIRCLE, scale: String(feature.getId()) === this.selected ? 10 : 6, fillColor: String(feature.getId()) === this.selected ? '#def55e' : '#613dad', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 }, zIndex: String(feature.getId()) === this.selected ? 2 : 1 }));
      if (!fit) return;
      if (!this.matches.length && this.origin) { this.map.setCenter(this.origin); this.map.setZoom(10); return; }
      if (!this.matches.length) return;
      const bounds = new this.maps.LatLngBounds();
      this.matches.forEach(store => bounds.extend({ lat: store.lat, lng: store.lng }));
      if (this.origin) bounds.extend(this.origin);
      this.map.fitBounds(bounds, 40);
      this.maps.event.addListenerOnce(this.map, 'idle', () => { if (this.map.getZoom() > 14) this.map.setZoom(14); });
    }
    selectStore(id) {
      const index = this.matches.findIndex(store => String(store.id) === id);
      if (index < 0) return;
      const store = this.matches[index]; this.selected = id;
      this.pageSize = Math.max(this.pageSize, Math.ceil((index + 1) / 20) * 20);
      this.renderList();
      const card = [...this.results.children].find(node => node.dataset.storeId === id);
      if (card) this.querySelector('.pw-locator__list-panel').scrollTop = card.offsetTop - this.results.offsetTop;
      if (!this.map) { this.showNotice('The map is unavailable. Use the Directions link to open this store in Google Maps.'); return; }
      this.map.data.revertStyle();
      const content = element('div', 'pw-locator__info');
      content.append(element('strong', '', store.name), element('p', '', [store.address, store.city, store.state, store.zip].filter(Boolean).join(', ')), this.makeLink('Get directions', this.directions(store)));
      this.info.setContent(content); this.info.setPosition({ lat: store.lat, lng: store.lng }); this.info.open({ map: this.map, shouldFocus: false });
      this.map.panTo({ lat: store.lat, lng: store.lng }); if (this.map.getZoom() < 12) this.map.setZoom(12);
    }
    setView(view) {
      this.querySelector('[data-view-mode]').dataset.viewMode = view;
      this.querySelectorAll('[data-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.view === view)));
      if (this.maps && this.map) this.maps.event.trigger(this.map, 'resize');
    }
  }
  customElements.define('pw-store-locator', StoreLocator);
})();
